
import React, { useState, useEffect, useMemo } from 'react';
import useSWR from 'swr';
import { DEFAULT_PAYMENT_OPTIONS, DeliveryMethod, PaymentMethod, PaymentOptions, CartItem, Product, User } from '../../types';
import { CheckoutForm, CheckoutFields } from './CheckoutForm';
import { DeliveryChoice } from './DeliveryChoice';
import { PaymentChoice } from './PaymentChoice';
import { StripePayment } from './StripePayment';
import { X, ShoppingCart, Minus, Plus, Trash2, AlertTriangle, Coffee } from 'lucide-react';
import { db } from '../../services/db';
import type { CheckoutDelivery, OrderReceipt } from '../../services/db/orders';
import { cartWeight, ShippingQuote } from '../../services/shipping';
import { homeDeliveryShort } from '../../api/_lib/deliveryText';
import { stripeConfigured } from '../../services/stripeClient';
import { effectiveVatRate, vatBreakdown } from '../../services/vat';
import { allocate, applyChoices, bundleContentsText, choicesKey, bundleItemsFor, isBundle, productImage, resolveBundle } from '../../services/bundles';
import { VatLines } from './VatLines';
import { trackBeginCheckout } from '../../services/attribution';
import { StoreImage } from '../ui/StoreImage';

interface CartDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    cart: CartItem[];
    /** Alla produkter, för paketens innehåll. */
    catalog?: Product[];
    onRemove: (line: CartItem) => void;
    onUpdateQuantity: (line: CartItem, delta: number) => void;
    cartTotal: number;
    cartCount: number;
    student?: User;
    /** Gruppens betalsätt (admin väljer per grupp). Saknas = betala direkt till säljaren. */
    paymentOptions?: PaymentOptions;
    onSubmitOrder: (form: CheckoutFields, studentCode: string, offerDeclined: boolean, delivery: CheckoutDelivery) => Promise<void>;
    isSubmitting: boolean;
    isCampaignEnded?: boolean; 
    /** Gruppens sista säljdag. Hemleveranser skickas när den passerat. */
    campaignEnd?: number;
    /** Hemleverans som väntar på betalning: kassan visar Stripes betalfält. */
    pendingPayment?: OrderReceipt | null;
    onPaid: (status: 'succeeded' | 'processing') => void;
    onBeforeRedirect: () => void;
    onCancelPayment: () => void;
}

export const CartDrawer: React.FC<CartDrawerProps> = ({ 
    isOpen, onClose, cart, catalog = [], onRemove, onUpdateQuantity, cartTotal, cartCount, student, paymentOptions, onSubmitOrder, isSubmitting, isCampaignEnded, campaignEnd,
    pendingPayment, onPaid, onBeforeRedirect, onCancelPayment
}) => {
    // Local form state for checkout
    const [studentCode, setStudentCode] = useState('');
    const [form, setForm] = useState<CheckoutFields>({ name: '', phone: '', email: '', address: '', postalCode: '', city: '' });
    const [delivery, setDelivery] = useState<DeliveryMethod>('PICKUP');
    const [payment, setPayment] = useState<PaymentMethod | null>(null);
    // Erbjudandemejlet efter kampanjen: kunden kan tacka nej i kassan.
    const [offerDeclined, setOfferDeclined] = useState(false);

    // Lock body scroll when drawer is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    // Statistik (bara efter samtycke): varukorgen med kassan öppnas. En gång per öppning.
    useEffect(() => {
        if (!isOpen || cart.length === 0) return;
        trackBeginCheckout(cart.map(i => ({
            item_id: i.product.id, item_name: i.product.name, item_variant: i.variant?.name,
            price: i.variant ? i.variant.price : i.product.price, quantity: i.quantity,
        })));
    }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    // Escape stänger varukorgen (tangentbordsanvändare).
    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [isOpen, onClose]);

    // Gruppens betalsätt. Betalning i kassan och hemleverans kräver Stripes publika nyckel.
    const options = paymentOptions || DEFAULT_PAYMENT_OPTIONS;
    const stripeAvailable = options.stripe && stripeConfigured();
    const homeAvailable = stripeAvailable && options.homeDelivery;
    const pickupMethods: PaymentMethod[] = [
        // Endast hemleverans: kortbetalning finns inte vid hämtning, då betalar kunden säljaren.
        ...(stripeAvailable && !options.stripeHomeOnly ? ['STRIPE' as const] : []),
        // Swish visas bara när säljaren fyllt i sitt nummer.
        ...(options.sellerSwish && student?.swishNumber ? ['SWISH' as const] : []),
        ...(options.sellerDirect ? ['SELLER' as const] : []),
    ];
    // Inget sätt att betala vid hämtning men hemleverans finns: gruppen säljer bara med hemleverans.
    const pickupPossible = pickupMethods.length > 0 || !homeAvailable;
    if (pickupMethods.length === 0) pickupMethods.push('SELLER');
    // Fraktpriset räknas i databasen, samma beräkning som när ordern läggs.
    const { missing: missingWeight } = useMemo(() => cartWeight(cart, catalog), [cart, catalog]);
    const quoteItems = useMemo(() => cart.map(i => ({ productId: i.product.id, variantId: i.variant?.id, quantity: i.quantity, ...(i.choices ? { choices: i.choices } : {}) })), [cart]);
    const { data: serverQuote } = useSWR(
        isOpen && homeAvailable && cart.length > 0 && missingWeight.length === 0 ? ['shipping-quote', JSON.stringify(quoteItems)] : null,
        () => db.quoteHomeShipping(quoteItems),
        { revalidateOnFocus: false, keepPreviousData: true }
    );
    const quote: ShippingQuote = missingWeight.length > 0
        ? { ok: false, reason: `${missingWeight.join(', ')} kan inte skickas hem än.` }
        : serverQuote ?? { ok: false, reason: 'Hämtar fraktpris…' };
    // Ett säkert nej (vara utan vikt, för tungt, avstängt), inte bara att priset laddas.
    const homeRefused = missingWeight.length > 0 || (serverQuote !== undefined && !serverQuote.ok);

    // Går hemleverans inte längre (t.ex. en vara utan vikt lades till) faller valet tillbaka på hämtning,
    // om hämtning finns. Säljer gruppen bara med hemleverans är det det enda valet.
    useEffect(() => {
        if (delivery === 'HOME' && homeRefused && pickupPossible) setDelivery('PICKUP');
        if (delivery === 'PICKUP' && !pickupPossible) setDelivery('HOME');
    }, [delivery, homeRefused, pickupPossible]);

    // Samma frakt som visas nedan (hooks måste ligga före den tidiga returen).
    const vatShipping = delivery === 'HOME' && quote.ok ? quote.price : 0;
    // Moms per sats för visning. Databasen räknar om den när ordern läggs.
    const { data: categories } = useSWR(isOpen ? 'product-categories' : null, () => db.getCategories(), { revalidateOnFocus: false });
    const cartVat = useMemo(() => {
        if (!categories) return undefined;
        const byId = new Map(categories.map(c => [c.id, c]));
        const lines = cart.flatMap(i => {
            const price = i.variant ? i.variant.price : i.product.price;
            // Paket: priset fördelas på delarna, varje del med sin moms (som ordern).
            if (isBundle(i.product)) {
                const parts = resolveBundle(applyChoices(bundleItemsFor(i.product, i.variant), i.choices), catalog, categories);
                if (parts.length === 0 || parts.some(p => p.vatRate === null)) return [null];
                const shares = allocate(price, parts);
                return parts.map((p, k) => ({ rate: p.vatRate as number, amount: shares[k] * i.quantity }));
            }
            const c = i.product.categoryId ? byId.get(i.product.categoryId) : undefined;
            return [c ? { rate: effectiveVatRate(c), amount: price * i.quantity } : null];
        });
        return lines.every(Boolean) ? vatBreakdown(lines as { rate: number; amount: number }[], vatShipping) : undefined;
    }, [categories, cart, catalog, vatShipping]);
    const contentsOf = (line: CartItem): string | null =>
        isBundle(line.product) ? bundleContentsText(resolveBundle(applyChoices(bundleItemsFor(line.product, line.variant), line.choices), catalog, [])) || null : null;

    if (!isOpen) return null;

    const isHome = delivery === 'HOME' && quote.ok;
    const shipping = isHome && quote.ok ? quote.price : 0;
    const sellerFirstName = student?.name?.split(' ')[0] || 'säljaren';
    // Hemleverans betalas alltid i kassan. Vid hämtning: kundens val, annars gruppens första sätt.
    const payMethod: PaymentMethod = isHome ? 'STRIPE' : (payment && pickupMethods.includes(payment) ? payment : pickupMethods[0]);
    const paysInCheckout = payMethod === 'STRIPE';

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmitOrder(form, studentCode, offerDeclined, isHome
            ? { method: 'HOME', paymentMethod: 'STRIPE', postalCode: form.postalCode, city: form.city }
            : { method: 'PICKUP', paymentMethod: payMethod });
    };

    return (
        <div className="fixed inset-0 z-[100] flex justify-end" role="dialog" aria-modal="true" aria-labelledby="cart-drawer-title">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-stone-900/60 backdrop-blur-sm transition-opacity" onClick={onClose}></div>
          
          {/* Drawer Panel */}
          <div className="relative bg-white w-full max-w-lg h-full shadow-2xl flex flex-col animate-slide-in-right">
            
            {/* Drawer Header */}
            <div className="p-5 border-b border-stone-100 flex justify-between items-center bg-[#FAF9F8] flex-shrink-0 z-10">
                <div>
                    <h2 id="cart-drawer-title" className="text-lg md:text-xl font-serif font-bold text-coffee-900">Din Varukorg</h2>
                    <p className="text-xs text-stone-500 font-medium">{cartCount} {cartCount === 1 ? 'produkt' : 'produkter'}</p>
                </div>
                <button type="button" onClick={onClose} aria-label="Stäng varukorgen" className="p-2 hover:bg-stone-200 rounded-full text-stone-500 transition-colors">
                    <X size={20} />
                </button>
            </div>

            {/* Main Content - Single Scrollable Area */}
            <div className="flex-1 overflow-y-auto custom-scrollbar bg-[#FDFBF9]">
                
                {cart.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-center space-y-4 text-stone-400 p-8 min-h-[300px]">
                        <ShoppingCart size={48} strokeWidth={1} />
                        <p className="text-sm font-medium">Varukorgen är tom.</p>
                        <button onClick={onClose} className="text-coffee-600 text-xs font-black uppercase tracking-widest hover:underline">
                            Börja handla
                        </button>
                    </div>
                ) : (
                    <>
                        {/* Compact Product List */}
                        <div className="p-4 space-y-3">
                            {cart.map((item, idx) => {
                                const price = item.variant ? item.variant.price : item.product.price;
                                const key = `${item.product.id}-${item.variant?.id || 'base'}-${choicesKey(item.choices)}-${idx}`;
                                
                                return (
                                    <div key={key} className="flex gap-3 items-center bg-white p-2.5 rounded-xl border border-stone-100 shadow-sm relative group">
                                        
                                        {/* Image */}
                                        {/* Paket utan egen bild får den första delens bild. */}
                                        <div className="w-14 h-14 rounded-lg bg-stone-100 overflow-hidden flex-shrink-0 border border-stone-50 flex items-center justify-center text-stone-300">
                                            {(() => {
                                                const src = productImage(item.product, item.variant, catalog, item.choices);
                                                return src
                                                    ? <StoreImage src={src} width={256} alt="" loading="lazy" className="w-full h-full object-cover" />
                                                    : <Coffee size={20} aria-hidden="true" />;
                                            })()}
                                        </div>
                                        
                                        {/* Info */}
                                        <div className="flex-1 min-w-0 pr-2">
                                            <div className="flex justify-between items-start">
                                                <h4 className="font-bold text-stone-800 text-xs md:text-sm truncate">{item.product.name}</h4>
                                            </div>
                                            {item.variant && (
                                                <p className="text-[10px] text-stone-500 font-medium truncate">{item.variant.name}</p>
                                            )}
                                            {(() => {
                                                // Paket: bara innehållet. Pris och moms per del står på kvittot.
                                                const summary = contentsOf(item);
                                                return summary ? <p className="text-[10px] text-stone-500 leading-snug">Innehåller {summary}</p> : null;
                                            })()}
                                            <div className="text-xs font-black text-coffee-600 mt-1">{price * item.quantity} kr</div>
                                        </div>

                                        {/* Controls: låsta under betalningen, så att beloppet hos Stripe stämmer med varukorgen */}
                                        {pendingPayment ? (
                                            <span className="text-xs font-bold text-stone-500 whitespace-nowrap">{item.quantity} st</span>
                                        ) : (
                                        <div className="flex flex-col items-end gap-2">
                                            <button 
                                                type="button"
                                                aria-label={`Ta bort ${item.product.name}`}
                                                onClick={() => onRemove(item)} 
                                                className="absolute -top-2 -right-2 bg-white text-stone-300 hover:text-red-500 hover:border-red-200 border border-stone-100 shadow-sm rounded-full p-1 transition-colors"
                                            >
                                                <Trash2 size={12}/>
                                            </button>
                                            
                                            <div className="flex items-center gap-2 bg-stone-50 px-1.5 py-1 rounded-lg border border-stone-100 mt-3">
                                                <button type="button" aria-label={`Minska antal ${item.product.name}`} onClick={() => onUpdateQuantity(item, -1)} className="p-1 hover:text-coffee-600 text-stone-400 active:scale-90"><Minus size={10}/></button>
                                                <span className="text-xs font-bold text-stone-700 min-w-[12px] text-center" aria-live="polite">{item.quantity}</span>
                                                <button type="button" aria-label={`Öka antal ${item.product.name}`} onClick={() => onUpdateQuantity(item, 1)} className="p-1 hover:text-coffee-600 text-stone-400 active:scale-90"><Plus size={10}/></button>
                                            </div>
                                        </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Summary & Form Section */}
                        <div className="p-5 md:p-6 bg-white border-t border-stone-100 shadow-[0_-4px_20px_rgba(0,0,0,0.02)]">
                            
                            {/* Säljperioden är slut: databasen tar inte emot fler ordrar, så kassan visas inte. */}
                            {isCampaignEnded && !pendingPayment ? (
                                <div className="bg-amber-50 border border-amber-100 p-4 rounded-xl flex gap-3 items-start" role="status">
                                    <AlertTriangle size={18} className="text-amber-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
                                    <div>
                                        <h4 className="text-sm font-bold text-amber-900">Säljperioden är slut</h4>
                                        <p className="text-xs text-amber-800 leading-relaxed mt-1">
                                            Det går inte att beställa från {sellerFirstName} längre. Tack för att du ville stötta!
                                        </p>
                                    </div>
                                </div>
                            ) : pendingPayment?.clientSecret ? (
                                <StripePayment
                                    clientSecret={pendingPayment.clientSecret}
                                    goods={pendingPayment.totalAmount ?? cartTotal}
                                    shipping={pendingPayment.shippingAmount}
                                    isHome={pendingPayment.deliveryMethod === 'HOME'}
                                    weightGrams={pendingPayment.shippingWeightGrams}
                                    vatBreakdown={pendingPayment.vatBreakdown}
                                    itemCount={cartCount}
                                    email={pendingPayment.customerEmail}
                                    onPaid={onPaid}
                                    onBeforeRedirect={onBeforeRedirect}
                                    onBack={onCancelPayment}
                                />
                            ) : (
                                <>
                                    {homeAvailable && (
                                        <DeliveryChoice
                                            value={isHome || !pickupPossible ? 'HOME' : 'PICKUP'}
                                            onChange={setDelivery}
                                            sellerFirstName={sellerFirstName}
                                            quote={quote}
                                            deliveryWhen={homeDeliveryShort(campaignEnd)}
                                            pickupEnabled={pickupPossible}
                                        />
                                    )}
                                    {!isHome && pickupPossible && (
                                        <PaymentChoice methods={pickupMethods} value={payMethod} onChange={setPayment} sellerFirstName={sellerFirstName} />
                                    )}

                                    <div className="mb-6 pb-4 border-b border-stone-100 border-dashed space-y-1.5">
                                        <div className="flex justify-between text-sm text-stone-600">
                                            <span>Varor, {cartCount} {cartCount === 1 ? 'produkt' : 'produkter'}</span>
                                            <span className="font-bold">{cartTotal} kr</span>
                                        </div>
                                        <div className="flex justify-between text-sm text-stone-600">
                                            <span>{isHome ? 'Frakt' : `Hämtas hos ${sellerFirstName}`}</span>
                                            <span className="font-bold">{`${shipping} kr`}</span>
                                        </div>
                                        <div className="flex justify-between items-center pt-2">
                                            <span className="text-xs font-black uppercase tracking-widest text-stone-500">{paysInCheckout ? 'Totalt att betala' : `Att betala till ${sellerFirstName}`}</span>
                                            <span className="text-2xl font-serif font-bold text-coffee-900">{cartTotal + shipping} kr</span>
                                        </div>
                                        {paysInCheckout ? <VatLines lines={cartVat} /> : <p className="text-[11px] text-stone-400">Priserna är inklusive moms.</p>}
                                        {!paysInCheckout && (
                                            <p className="text-[11px] text-stone-400 leading-snug pt-1">
                                                {payMethod === 'SWISH'
                                                    ? <>Du swishar {sellerFirstName} efter beställningen. Betala aldrig till någon annan än din säljare.</>
                                                    : <>Du betalar {sellerFirstName} när du får kaffet, eller när {sellerFirstName} hör av sig. Betala aldrig till någon annan än din säljare.</>}
                                            </p>
                                        )}
                                    </div>

                                    <CheckoutForm 
                                        student={student}
                                        studentCode={studentCode}
                                        setStudentCode={setStudentCode}
                                        form={form}
                                        setForm={setForm}
                                        deliveryMethod={isHome ? 'HOME' : 'PICKUP'}
                                        paysInCheckout={paysInCheckout}
                                        offerDeclined={offerDeclined}
                                        setOfferDeclined={setOfferDeclined}
                                        sellerFirstName={sellerFirstName}
                                        onSubmit={handleSubmit}
                                        isSubmitting={isSubmitting}
                                    />
                                </>
                            )}
                        </div>
                    </>
                )}
            </div>

          </div>
        </div>
    );
};
